import * as React from "react"
import { router } from "@inertiajs/react"
import { Layers, ListChecks, RotateCcw } from "lucide-react"
import ReactMarkdown from "react-markdown"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import type { ChatMessage } from "@/types/assistant"

/** Smallest sizes the generator offers: an answer is a small amount of source material. */
const DECK_COUNT = "10"
const QUIZ_COUNT = "5"

export function MessageBubble({
  message,
  conversationId,
  conversationTitle,
  showActions = false,
}: {
  message: ChatMessage
  conversationId: number
  conversationTitle: string
  showActions?: boolean
}) {
  const intl = useIntl()
  if (message.role === "user") {
    return (
      <div className="ml-auto max-w-[85%] rounded-lg bg-accent-faded px-4 py-3">
        <p className="whitespace-pre-wrap break-words">{message.text}</p>
      </div>
    )
  }

  if (message.status === "failed") {
    return (
      <div className="callout callout-danger max-w-[85%]" role="alert">
        {message.text && <AssistantText text={message.text} />}
        <p className={message.text ? "mt-3" : undefined}>{message.error_message ?? intl.formatMessage({ id: "assistant.generic_error" })}</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() =>
            router.post(`/conversations/${conversationId}/messages/${message.id}/retry`, {}, { preserveScroll: true })
          }
        >
          <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "assistant.try_again" })}
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-[85%]" aria-busy={message.status === "streaming"}>
      {message.text ? (
        <AssistantText text={message.text} streaming={message.status === "streaming"} />
      ) : (
        <p className="text-sm text-ink-muted" role="status">
          <span className="inline-flex gap-1" aria-hidden>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink-muted" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink-muted [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink-muted [animation-delay:300ms]" />
          </span>{" "}
          {intl.formatMessage({ id: "assistant.thinking" })}
        </p>
      )}
      {showActions && message.status === "complete" && message.text && (
        <GenerateActions text={message.text} title={conversationTitle} />
      )}
    </div>
  )
}

/** Hands the answer to the existing "generate from pasted text" flow. */
function GenerateActions({ text, title }: { text: string; title: string }) {
  const intl = useIntl()
  const [busy, setBusy] = React.useState(false)
  const generate = (kind: "deck" | "quiz") =>
    router.post(
      "/generations",
      {
        kind,
        source_mode: "text",
        text,
        source_title: title,
        count: kind === "deck" ? DECK_COUNT : QUIZ_COUNT,
        difficulty: "standard",
      },
      { onStart: () => setBusy(true), onFinish: () => setBusy(false) },
    )

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => generate("deck")}>
        <Layers className="h-4 w-4" /> {intl.formatMessage({ id: "assistant.create_cards" })}
      </Button>
      <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => generate("quiz")}>
        <ListChecks className="h-4 w-4" /> {intl.formatMessage({ id: "assistant.create_quiz" })}
      </Button>
    </div>
  )
}

function AssistantText({ text, streaming = false }: { text: string; streaming?: boolean }) {
  return (
    <div className="body-content break-words">
      <ReactMarkdown
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer nofollow">
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
      {streaming && <span className="inline-block h-4 w-1.5 animate-pulse bg-ink-muted align-middle" aria-hidden />}
    </div>
  )
}
